import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import LinearProgress from '@mui/material/LinearProgress';
import MuiLink from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import {
	useAPI,
	useForm,
	useQuery,
	useMutation,
	type APIInput,
	useToastsContext,
} from '@mybonus/ui';
import { ReactNode, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Page, ErrorPopup, DateTime } from '../../components';
import { getWithdrawlReviewChipColor } from '../../helpers';

export function TH(props: { children?: ReactNode }) {
	return <TableCell sx={{ fontWeight: 'bold' }}>{props.children}</TableCell>;
}

export function WithdrawalReviewDetailView() {
	const { api } = useAPI();
	const toasts = useToastsContext();

	const params = useParams();
	const input = useMemo(() => ({ id: Number(params.id) }), [params.id]);
	const single = useQuery(
		'admin.withdrawalReview.single',
		(args) => api.admin.withdrawalReview.single.query(args),
		input,
	);
	const decide = useMutation<APIInput['admin']['withdrawalReview']['decide']>(
		(input) => api.admin.withdrawalReview.decide.mutate(input),
		{
			onSuccess: () => {
				toasts.addToast({
					type: 'success',
					message: 'Withdrawal review has been handled.',
					timeout: 5000,
				});
				single.load({ refetch: true, bypassCache: true });
			},
		},
	);

	const form = useForm<{
		reasonMessage: string;
		decision: 'APPROVED' | 'DENIED';
	}>({
		onSubmit: () => {
			if (single.data?.id) {
				decide.mutate({
					id: single.data.id,
					action: {
						decision: form.data.decision,
						reasonMessage: form.data.reasonMessage,
					},
				});
			}
		},
		validations: {
			decision: {
				required: {
					message: 'Decision is required',
				},
			},
		},
		initialValues: {
			decision: 'APPROVED',
			reasonMessage: '',
		},
	});

	return (
		<Page title={`Handling Withdrawal review #${input.id}`} width="contained">
			{single.isLoading && <LinearProgress />}

			{single.data && single.data.decision !== 'PENDING' && (
				<Alert severity="warning">
					This review is already processed and cannot be processed again.
				</Alert>
			)}

			<Stack spacing={3}>
				{!!single.data && (
					<TableContainer component={Paper}>
						<Table>
							<TableBody>
								<TableRow>
									<TH>ID</TH>
									<TableCell>{single.data.id}</TableCell>
								</TableRow>

								<TableRow>
									<TH>Status</TH>

									<TableCell>
										<Chip
											label={single.data.decision}
											size="small"
											color={getWithdrawlReviewChipColor(single.data.decision)}
										/>
									</TableCell>
								</TableRow>

								<TableRow>
									<TH>Amount</TH>
									<TableCell>{single.data.amount.text}</TableCell>
								</TableRow>

								<TableRow>
									<TH>User</TH>
									<TableCell>
										<MuiLink component={Link} to={`/app/users/${single.data.user.id}`}>
											{single.data.user.email}
										</MuiLink>{' '}
										({single.data.user.id})
									</TableCell>
								</TableRow>

								<TableRow>
									<TH>Handled by admin</TH>
									<TableCell>{single.data.handledByAdmin?.email || '-'}</TableCell>
								</TableRow>

								{single.data.decision === 'DENIED' && (
									<TableRow>
										<TH>Reason message</TH>
										<TableCell>{single.data.reasonMessage || '-'}</TableCell>
									</TableRow>
								)}

								<TableRow>
									<TH>Updated</TH>
									<TableCell>
										<DateTime date={single.data.updatedAt} />
									</TableCell>
								</TableRow>

								<TableRow>
									<TH>Created</TH>
									<TableCell>
										<DateTime date={single.data.createdAt} />
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</TableContainer>
				)}

				{single.data?.decision === 'PENDING' && (
					<Stack component={Paper} sx={{ p: 2 }} spacing={1}>
						<FormControl>
							<InputLabel id="decisionFieldLabel">Decision</InputLabel>
							<Select
								labelId="decisionFieldLabel"
								value={form.data.decision}
								label="Decision"
								onChange={(e) => form.handleChange('decision')(e.target.value)}
								required
								size="small"
							>
								<MenuItem value="APPROVED">Approve withdrawal</MenuItem>
								<MenuItem value="DENIED">Deny withdrawal</MenuItem>
							</Select>
						</FormControl>

						{form.data.decision === 'DENIED' && (
							<TextField
								required={form.data.decision === 'DENIED'}
								type="text"
								value={form.data.reasonMessage || ''}
								label="Reason Message"
								error={!!form.errors.reasonMessage}
								size="small"
								onChange={(e) => form.handleChange('reasonMessage')(e.target.value)}
							/>
						)}

						<Box>
							<LoadingButton
								variant="contained"
								disabled={decide.isLoading}
								loading={decide.isLoading}
								onClick={form.handleSubmit}
							>
								Submit
							</LoadingButton>
						</Box>
					</Stack>
				)}

				<ErrorPopup error={single.error} onRetry={single.load} onClose={single.reset} />
				<ErrorPopup error={decide.error} onClose={decide.reset} />
			</Stack>
		</Page>
	);
}
